import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { get } from '../../../Services/http';
import { translate } from '../../../Services/translation';

const QUERY_PARAM = 'gf';
const TEXT_PARAM = 'q';

const STEP_TYPE = {
    Category: 'Category',
    ProductField: 'ProductField',
    FreeText: 'FreeText',
};

const formatText = (template, value) =>
    (template || '').replace('{0}', value || '');

const MultiSelectDropdown = ({
    id,
    label,
    options = [],
    selected,
    disabled,
    placeholder,
    onToggle,
}) => {
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);
    const selectedNames = options
        .filter((option) =>
            selected.has((option.slug || option.name || '').toLowerCase())
        )
        .map((option) => option.name);

    useEffect(() => {
        if (!open) {
            return undefined;
        }

        const onPointerDown = (event) => {
            if (rootRef.current && !rootRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                setOpen(false);
            }
        };

        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open]);

    return (
        <div
            ref={rootRef}
            className={`guided-category-filter__dropdown${
                open ? ' is-open' : ''
            }${disabled ? ' is-disabled' : ''}`}
        >
            <button
                type="button"
                id={id}
                className="guided-category-filter__dropdown-toggle"
                disabled={disabled}
                aria-haspopup="true"
                aria-expanded={open}
                onClick={() => setOpen((current) => !current)}
            >
                <span className="guided-category-filter__dropdown-value">
                    {selectedNames.length
                        ? selectedNames.join(', ')
                        : placeholder}
                </span>
            </button>
            {open && (
                <ul
                    className="guided-category-filter__dropdown-menu"
                    aria-label={label}
                >
                    {options.length === 0 && (
                        <li className="guided-category-filter__empty">
                            {placeholder}
                        </li>
                    )}
                    {options.map((option) => {
                        const optionKey = (
                            option.slug ||
                            option.name ||
                            ''
                        ).toLowerCase();
                        const checked = selected.has(optionKey);
                        return (
                            <li key={option.slug || option.name}>
                                <label className="guided-category-filter__dropdown-option">
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={(event) =>
                                            onToggle(
                                                option.slug || option.name,
                                                event.target.checked
                                            )
                                        }
                                    />
                                    <span>{option.name}</span>
                                </label>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

const replaceSearchResultPreservingFilters = (productsViewHtml) => {
    const container = document.createElement('div');
    container.innerHTML = productsViewHtml;
    const existingResult = document.querySelector('#search-result');
    const tempResult = container.querySelector('#search-result');
    if (!existingResult || !tempResult) {
        return null;
    }

    const tempGuided = tempResult.querySelector('#guidedCategoryFilter');
    let guidedUpdate = null;
    if (tempGuided) {
        guidedUpdate = {
            steps: tempGuided.dataset.steps
                ? JSON.parse(tempGuided.dataset.steps)
                : [],
            selectedPath: tempGuided.getAttribute('data-selected-path') || '',
            showReset: tempGuided.getAttribute('data-show-reset') === 'true',
            rootUrl: tempGuided.getAttribute('data-root-url') || '',
            fieldStepIds: tempGuided.dataset.fieldStepIds
                ? JSON.parse(tempGuided.dataset.fieldStepIds)
                : [],
        };
    }

    ['guidedCategoryFilter', 'facetedSearchCompact'].forEach((id) => {
        const existing = existingResult.querySelector(`#${id}`);
        const temp = tempResult.querySelector(`#${id}`);
        if (existing && temp) {
            temp.parentNode.replaceChild(existing, temp);
        }
    });

    existingResult.parentNode.replaceChild(tempResult, existingResult);

    if (window.__litium && typeof window.__litium.bootstrapComponents === 'function') {
        window.__litium.bootstrapComponents();
    }

    return guidedUpdate;
};

const clearFieldParams = (url, fieldStepIds) => {
    (fieldStepIds || []).forEach((fieldId) => {
        url.searchParams.delete(fieldId);
    });
};

const setFieldParams = (url, fieldId, values) => {
    url.searchParams.delete(fieldId);
    (values || []).forEach((value) => {
        if (value) {
            url.searchParams.append(fieldId, value);
        }
    });
};

const GuidedCategoryFilter = ({
    steps: initialSteps = [],
    selectedPath: initialSelectedPath = '',
    rootUrl: initialRootUrl = '',
    showReset: initialShowReset = false,
    fieldStepIds: initialFieldStepIds = [],
}) => {
    const [steps, setSteps] = useState(initialSteps || []);
    const [selectedPath, setSelectedPath] = useState(initialSelectedPath || '');
    const [rootUrl, setRootUrl] = useState(initialRootUrl || '');
    const [showReset, setShowReset] = useState(!!initialShowReset);
    const [fieldStepIds, setFieldStepIds] = useState(initialFieldStepIds || []);
    const [isLoading, setIsLoading] = useState(false);
    const [freeTextDraft, setFreeTextDraft] = useState(() => {
        const freeTextStep = (initialSteps || []).find(
            (s) => s.type === STEP_TYPE.FreeText
        );
        return freeTextStep?.selectedSlug || '';
    });

    const chooseTemplate = translate('guidedfilter.choose');
    const chooseFallback = translate('guidedfilter.choose.fallback');
    const summaryTemplate = translate('guidedfilter.summary');
    const resetText = translate('guidedfilter.reset');
    const freeTextPlaceholder = translate('guidedfilter.freetext.placeholder');

    const getStepLabel = (step, index) =>
        step.label || translate(`guidedfilter.level.${index + 1}`);

    const applyGuidedUpdate = useCallback((guidedUpdate) => {
        if (!guidedUpdate) {
            return;
        }
        setSteps(guidedUpdate.steps || []);
        setSelectedPath(guidedUpdate.selectedPath || '');
        setShowReset(!!guidedUpdate.showReset);
        if (guidedUpdate.rootUrl) {
            setRootUrl(guidedUpdate.rootUrl);
        }
        if (guidedUpdate.fieldStepIds) {
            setFieldStepIds(guidedUpdate.fieldStepIds);
        }
        const freeTextStep = (guidedUpdate.steps || []).find(
            (s) => s.type === STEP_TYPE.FreeText
        );
        setFreeTextDraft(freeTextStep?.selectedSlug || '');
    }, []);

    const fetchAndApply = useCallback(
        async (nextUrl, { pushHistory = true } = {}) => {
            setIsLoading(true);
            document
                .querySelectorAll('[data-productlist]')
                .forEach((el) => el.classList.add('is-guided-filter-loading'));

            try {
                if (pushHistory) {
                    window.history.pushState(
                        { guidedFilter: true },
                        '',
                        nextUrl.pathname + nextUrl.search + nextUrl.hash
                    );
                }

                const queryString = nextUrl.searchParams.toString();
                const apiUrl = queryString
                    ? `/api/productFilter/withHtmlResult?${queryString}`
                    : '/api/productFilter/withHtmlResult';
                const response = await get(apiUrl);
                const result = await response.json();
                if (result && result.productsView) {
                    const guidedUpdate = replaceSearchResultPreservingFilters(
                        result.productsView
                    );
                    applyGuidedUpdate(guidedUpdate);
                }
            } catch (e) {
                window.location.assign(
                    nextUrl.pathname + nextUrl.search + nextUrl.hash
                );
            } finally {
                setIsLoading(false);
                document
                    .querySelectorAll('[data-productlist]')
                    .forEach((el) =>
                        el.classList.remove('is-guided-filter-loading')
                    );
            }
        },
        [applyGuidedUpdate]
    );

    const buildUrl = useCallback(
        ({ industrySlug, tags, text, clearTags = false }) => {
            const url = new URL(window.location.href);

            if (industrySlug) {
                url.searchParams.set(QUERY_PARAM, industrySlug);
            } else {
                url.searchParams.delete(QUERY_PARAM);
            }

            if (clearTags) {
                clearFieldParams(url, fieldStepIds);
            } else if (tags) {
                Object.entries(tags).forEach(([fieldId, values]) => {
                    setFieldParams(url, fieldId, values);
                });
            }

            if (text) {
                url.searchParams.set(TEXT_PARAM, text);
            } else if (text === '') {
                url.searchParams.delete(TEXT_PARAM);
            }

            url.searchParams.delete('page');
            return url;
        },
        [fieldStepIds]
    );

    const currentIndustrySlug = useMemo(() => {
        const industryStep = steps.find((s) => s.type === STEP_TYPE.Category);
        if (!industryStep || industryStep.isLocked) {
            // Industry comes from the category URL — do not mirror it in ?gf=.
            return '';
        }
        return industryStep.selectedSlug || selectedPath || '';
    }, [steps, selectedPath]);

    const currentFieldTags = useMemo(() => {
        const tags = {};
        steps
            .filter((s) => s.type === STEP_TYPE.ProductField && s.fieldId)
            .forEach((s) => {
                tags[s.fieldId] = [...(s.selectedValues || [])];
            });
        return tags;
    }, [steps]);

    useEffect(() => {
        const onPopState = () => {
            const url = new URL(window.location.href);
            fetchAndApply(url, { pushHistory: false });
        };
        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
    }, [fetchAndApply]);

    useEffect(() => {
        const freeTextStep = steps.find((s) => s.type === STEP_TYPE.FreeText);
        if (!freeTextStep?.isEnabled) {
            return undefined;
        }

        const handle = window.setTimeout(() => {
            const committed = freeTextStep.selectedSlug || '';
            if ((freeTextDraft || '') === committed) {
                return;
            }
            const url = buildUrl({
                industrySlug: currentIndustrySlug,
                tags: currentFieldTags,
                text: freeTextDraft || '',
            });
            fetchAndApply(url);
        }, 400);

        return () => window.clearTimeout(handle);
    }, [
        freeTextDraft,
        steps,
        buildUrl,
        currentIndustrySlug,
        currentFieldTags,
        fetchAndApply,
    ]);

    const selectedSummary = useMemo(() => {
        const parts = [];
        steps.forEach((step) => {
            if (step.type === STEP_TYPE.Category && step.selectedSlug) {
                const option = (step.options || []).find(
                    (o) =>
                        o.slug?.toLowerCase() ===
                        step.selectedSlug?.toLowerCase()
                );
                parts.push(option?.name || step.selectedSlug);
            } else if (
                step.type === STEP_TYPE.ProductField &&
                step.selectedValues?.length
            ) {
                const names = step.selectedValues.map((value) => {
                    const option = (step.options || []).find(
                        (o) =>
                            o.slug?.toLowerCase() === value?.toLowerCase() ||
                            o.name?.toLowerCase() === value?.toLowerCase()
                    );
                    return option?.name || value;
                });
                parts.push(names.join(', '));
            } else if (step.type === STEP_TYPE.FreeText && step.selectedSlug) {
                parts.push(`"${step.selectedSlug}"`);
            }
        });
        return parts.length ? parts.join(' / ') : null;
    }, [steps]);

    const onIndustryChange = (slug) => {
        if (isLoading) {
            return;
        }
        const url = buildUrl({
            industrySlug: slug || '',
            clearTags: true,
            text: freeTextDraft || '',
        });
        fetchAndApply(url);
    };

    const onFieldToggle = (fieldId, value, checked) => {
        if (isLoading || !fieldId || !value) {
            return;
        }
        const nextTags = { ...currentFieldTags };
        const current = new Set(nextTags[fieldId] || []);
        if (checked) {
            current.add(value);
        } else {
            current.delete(value);
        }
        nextTags[fieldId] = [...current];
        const url = buildUrl({
            industrySlug: currentIndustrySlug,
            tags: nextTags,
            text: freeTextDraft || '',
        });
        fetchAndApply(url);
    };

    const onReset = (event) => {
        event.preventDefault();
        if (isLoading) {
            return;
        }

        if (rootUrl) {
            const url = new URL(rootUrl, window.location.origin);
            url.searchParams.delete(QUERY_PARAM);
            url.searchParams.delete(TEXT_PARAM);
            clearFieldParams(url, fieldStepIds);
            const current = new URL(window.location.href);
            if (url.pathname === current.pathname) {
                fetchAndApply(url);
            } else {
                window.location.assign(url.pathname + url.search + url.hash);
            }
            return;
        }

        const url = buildUrl({
            industrySlug: '',
            clearTags: true,
            text: '',
        });
        fetchAndApply(url);
    };

    if (!steps.length) {
        return null;
    }

    const stepsStyle = {
        ['--guided-filter-cols']: steps.length,
    };

    return (
        <div
            className={`guided-category-filter__panel${
                isLoading ? ' is-loading' : ''
            }`}
        >
            <ol className="guided-category-filter__steps" style={stepsStyle}>
                {steps.map((step, index) => {
                    const label = getStepLabel(step, index);
                    const disabled =
                        !!step.isLocked || !step.isEnabled || isLoading;

                    if (step.type === STEP_TYPE.ProductField) {
                        const selected = new Set(
                            (step.selectedValues || []).map((v) =>
                                (v || '').toLowerCase()
                            )
                        );
                        const placeholder = label
                            ? formatText(chooseTemplate, label.toLowerCase())
                            : chooseFallback;
                        return (
                            <li
                                key={step.fieldId || step.index || index}
                                className="guided-category-filter__step"
                            >
                                <label
                                    className="guided-category-filter__label"
                                    htmlFor={`guided-filter-step-${index}`}
                                >
                                    <span className="guided-category-filter__step-number">
                                        {index + 1}.
                                    </span>{' '}
                                    {label}
                                </label>
                                <MultiSelectDropdown
                                    id={`guided-filter-step-${index}`}
                                    label={label}
                                    options={step.options || []}
                                    selected={selected}
                                    disabled={disabled}
                                    placeholder={placeholder}
                                    onToggle={(value, checked) =>
                                        onFieldToggle(
                                            step.fieldId,
                                            value,
                                            checked
                                        )
                                    }
                                />
                            </li>
                        );
                    }

                    if (step.type === STEP_TYPE.FreeText) {
                        return (
                            <li
                                key={`freetext-${step.index ?? index}`}
                                className="guided-category-filter__step"
                            >
                                <label
                                    className="guided-category-filter__label"
                                    htmlFor={`guided-filter-step-${index}`}
                                >
                                    <span className="guided-category-filter__step-number">
                                        {index + 1}.
                                    </span>{' '}
                                    {label ||
                                        translate('guidedfilter.freetext')}
                                </label>
                                <input
                                    id={`guided-filter-step-${index}`}
                                    type="search"
                                    className="guided-category-filter__input"
                                    value={freeTextDraft}
                                    disabled={disabled}
                                    placeholder={freeTextPlaceholder}
                                    onChange={(event) =>
                                        setFreeTextDraft(event.target.value)
                                    }
                                />
                            </li>
                        );
                    }

                    return (
                        <li
                            key={step.index ?? index}
                            className="guided-category-filter__step"
                        >
                            <label
                                className="guided-category-filter__label"
                                htmlFor={`guided-filter-step-${index}`}
                            >
                                <span className="guided-category-filter__step-number">
                                    {index + 1}.
                                </span>{' '}
                                {label}
                            </label>
                            <select
                                id={`guided-filter-step-${index}`}
                                className="guided-category-filter__select"
                                value={step.selectedSlug || ''}
                                disabled={disabled}
                                onChange={(event) =>
                                    onIndustryChange(event.target.value)
                                }
                            >
                                {!step.isLocked && (
                                    <option value="">
                                        {label
                                            ? formatText(
                                                  chooseTemplate,
                                                  label.toLowerCase()
                                              )
                                            : chooseFallback}
                                    </option>
                                )}
                                {(step.options || []).map((option) => (
                                    <option
                                        key={option.slug}
                                        value={option.slug}
                                    >
                                        {option.name}
                                    </option>
                                ))}
                            </select>
                        </li>
                    );
                })}
            </ol>

            <div
                className={`guided-category-filter__summary${
                    (showReset || selectedPath || selectedSummary) &&
                    selectedSummary
                        ? ''
                        : ' is-empty'
                }`}
            >
                {(showReset || selectedPath || selectedSummary) &&
                    selectedSummary && (
                        <>
                            <p>
                                {summaryTemplate.includes('{0}') ? (
                                    <>
                                        {summaryTemplate.split('{0}')[0]}
                                        <strong>{selectedSummary}</strong>
                                        {summaryTemplate.split('{0}')[1]}
                                    </>
                                ) : (
                                    <>
                                        {summaryTemplate}{' '}
                                        <strong>{selectedSummary}</strong>
                                    </>
                                )}
                            </p>
                            <a
                                href={rootUrl || '#'}
                                className="guided-category-filter__reset"
                                onClick={onReset}
                            >
                                {resetText}
                            </a>
                        </>
                    )}
            </div>
        </div>
    );
};

export default GuidedCategoryFilter;
